import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Thermometer, Activity, Shield, TrendingUp, DollarSign, Building2, Factory, Truck, Zap } from 'lucide-react';
import Navigation from '@/sections/Navigation';
import Footer from '@/sections/Footer';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const CaseStudy = () => {
  const navigate = useNavigate();
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      sectionRefs.current.forEach((ref) => {
        if (ref) {
          gsap.fromTo(
            ref,
            { opacity: 0, y: 30 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: 'expo.out',
              scrollTrigger: {
                trigger: ref,
                start: 'top 85%',
              },
            }
          );
        }
      });
    });

    return () => ctx.revert();
  }, []);

  const industries = [
    { name: 'Real Estate & Property Management', icon: Building2 },
    { name: 'Warehousing & Logistics', icon: Truck },
    { name: 'Manufacturing & Factories', icon: Factory },
    { name: 'Utilities & Infrastructure', icon: Zap },
    { name: 'Municipal Services', icon: Building2 },
    { name: 'Commercial Facilities', icon: Building2 },
  ];

  return (
    <div className="min-h-screen bg-kmg-bg">
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 lg:py-20 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <div className="absolute top-0 right-0 w-96 h-96 bg-kmg-primary/20 rounded-full blur-[150px]" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-kmg-accent/20 rounded-full blur-[150px]" />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white/70 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
            
            <div className="max-w-4xl">
              <span className="inline-block px-4 py-2 text-sm font-medium text-kmg-primary bg-kmg-primary/20 rounded-full mb-6">
                Case Study
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Real-Time Vision and Sensor Intelligence
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                Transforming reactive monitoring into proactive prevention through continuous sensing and real-time intelligence.
              </p>
            </div>
          </div>
        </section>

        {/* Hero Image with Content */}
        <section className="relative -mt-8 z-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative rounded-xl overflow-hidden shadow-card">
                <img
                  src="/caseStudy/caseStudy1.png"
                  alt="Vision and Sensor Intelligence System"
                  className="w-full h-auto"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  Continuous Monitoring, Continuous Intelligence
                </h3>
                <p className="text-slate-600 mb-6">
                  Our vision and sensor intelligence system transforms how organizations monitor and maintain physical environments. By combining visual feeds, thermal sensing, and environmental sensors, we create a comprehensive data layer that enables proactive decision-making.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-kmg-primary/10 rounded-lg flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 bg-kmg-primary rounded-full"></div>
                    </div>
                    <p className="text-slate-600 text-sm">Real-time anomaly detection across multiple signal types</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-kmg-primary/10 rounded-lg flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 bg-kmg-primary rounded-full"></div>
                    </div>
                    <p className="text-slate-600 text-sm">Edge processing for instant alerts and responses</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-kmg-primary/10 rounded-lg flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 bg-kmg-primary rounded-full"></div>
                    </div>
                    <p className="text-slate-600 text-sm">Historical pattern recognition for predictive insights</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* The Problem */}
        <section 
          ref={(el) => { sectionRefs.current[0] = el; }}
          className="relative py-32 bg-white overflow-hidden"
        >
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
            <div className="absolute top-1/3 right-0 w-96 h-96 bg-kmg-primary/5 rounded-full blur-[150px]" />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto mb-16">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
                The <span className="text-gradient">Problem</span>
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Most physical environments are still monitored reactively. Property managers find out pipes burst after damage is done. Factories discover machine failure after production is lost. Facilities rely on manual checks, clipboards, and delayed reports.
              </p>
            </div>

            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-card p-8 border border-slate-200">
              <p className="text-slate-800 font-medium mb-2">Human monitoring does not scale.</p>
              <p className="text-slate-600">
                It is inconsistent, expensive, and blind to subtle early signals. Small changes in temperature, vibration, movement, or timing are often invisible to people but critical to systems. By the time humans notice, the cost has already been incurred.
              </p>
            </div>
          </div>
        </section>

        {/* The Solution */}
        <section 
          ref={(el) => { sectionRefs.current[1] = el; }}
          className="relative py-32 bg-kmg-bg overflow-hidden"
        >
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
            <div className="absolute bottom-1/3 left-1/3 w-96 h-96 bg-kmg-accent/5 rounded-full blur-[150px]" />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto mb-16">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
                The <span className="text-gradient">Solution</span>
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                We deployed a camera and sensor-based monitoring system that continuously observes physical environments and converts real-world signals into structured, actionable data.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 max-w-6xl mx-auto">
              <div className="p-6 bg-white rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 border border-slate-200 group">
                <div className="w-12 h-12 flex items-center justify-center bg-kmg-primary/10 rounded-lg mb-4 group-hover:scale-110 transition-transform">
                  <Eye className="w-6 h-6 text-kmg-primary" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Visual Camera Feeds</h3>
                <p className="text-sm text-slate-600">Continuous visual monitoring and analysis</p>
              </div>
              <div className="p-6 bg-white rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 border border-slate-200 group">
                <div className="w-12 h-12 flex items-center justify-center bg-kmg-primary/10 rounded-lg mb-4 group-hover:scale-110 transition-transform">
                  <Thermometer className="w-6 h-6 text-kmg-primary" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Thermal Sensing</h3>
                <p className="text-sm text-slate-600">Temperature anomaly detection</p>
              </div>
              <div className="p-6 bg-white rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 border border-slate-200 group">
                <div className="w-12 h-12 flex items-center justify-center bg-kmg-primary/10 rounded-lg mb-4 group-hover:scale-110 transition-transform">
                  <Activity className="w-6 h-6 text-kmg-primary" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Environmental Sensors</h3>
                <p className="text-sm text-slate-600">Multi-signal data collection</p>
              </div>
              <div className="p-6 bg-white rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 border border-slate-200 group">
                <div className="w-12 h-12 flex items-center justify-center bg-kmg-primary/10 rounded-lg mb-4 group-hover:scale-110 transition-transform">
                  <Zap className="w-6 h-6 text-kmg-primary" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Real-Time Inference</h3>
                <p className="text-sm text-slate-600">Instant processing and alerts</p>
              </div>
            </div>

            <div className="max-w-4xl mx-auto bg-gradient-to-r from-kmg-primary to-kmg-accent rounded-xl p-8 text-white shadow-card">
              <p className="text-lg font-medium mb-2">
                Instead of relying on periodic human checks, the system monitors continuously, detects anomalies early, and creates a reliable data layer for operations teams.
              </p>
              <p className="text-white/90 italic text-sm">
                This is not surveillance for viewing. It is sensing for understanding.
              </p>
            </div>
          </div>
        </section>

        {/* What the System Detects */}
        <section 
          ref={(el) => { sectionRefs.current[2] = el; }}
          className="relative py-32 bg-white overflow-hidden"
        >
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
            <div className="absolute top-1/2 right-0 w-96 h-96 bg-kmg-primary/5 rounded-full blur-[150px]" />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto mb-16">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
                What the System <span className="text-gradient">Detects</span>
              </h2>
              <p className="text-lg text-slate-600">
                The system is designed to observe normal behavior, identify deviations, and surface insights before failure occurs.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Environmental */}
              <div className="bg-white rounded-xl shadow-card p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-kmg-primary/10 rounded-lg">
                    <Thermometer className="w-5 h-5 text-kmg-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Environmental & Infrastructure</h3>
                </div>
                <ul className="space-y-2 mb-4 text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">Temperature changes in pipes and HVAC systems</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">Early signs of freezing or overheating</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">Thermal anomalies indicating leaks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">Moisture buildup patterns</span>
                  </li>
                </ul>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <p className="text-xs font-medium text-slate-900 mb-1">Example</p>
                  <p className="text-xs text-slate-600">
                    Preventing pipe bursts in real estate during extreme cold by detecting temperature drops before freezing occurs.
                  </p>
                </div>
              </div>

              {/* Equipment */}
              <div className="bg-white rounded-xl shadow-card p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-kmg-primary/10 rounded-lg">
                    <Activity className="w-5 h-5 text-kmg-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Equipment & Machine Health</h3>
                </div>
                <ul className="space-y-2 mb-4 text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">Abnormal heat signatures in machinery</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">Irregular motion or vibration patterns</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">Idle time, overuse, or unsafe operation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">Early indicators of mechanical failure</span>
                  </li>
                </ul>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <p className="text-xs font-medium text-slate-900 mb-1">Example</p>
                  <p className="text-xs text-slate-600">
                    Detecting a failing motor before it overheats and damages surrounding equipment or halts production.
                  </p>
                </div>
              </div>

              {/* Operational */}
              <div className="bg-white rounded-xl shadow-card p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-kmg-primary/10 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-kmg-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Operational Activity</h3>
                </div>
                <ul className="space-y-2 mb-4 text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">Snowplow activity and timing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">Garbage collection confirmation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">Equipment usage verification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">Access patterns and movement flow</span>
                  </li>
                </ul>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <p className="text-xs font-medium text-slate-900 mb-1">Example</p>
                  <p className="text-xs text-slate-600">
                    Confirming snow removal occurred on schedule without manual check-ins or disputes.
                  </p>
                </div>
              </div>

              {/* Security */}
              <div className="bg-white rounded-xl shadow-card p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-kmg-primary/10 rounded-lg">
                    <Shield className="w-5 h-5 text-kmg-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Safety & Security</h3>
                </div>
                <ul className="space-y-2 mb-4 text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">Unexpected human presence</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">After hours movement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">Entry into restricted zones</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span className="text-sm">Behavioral anomalies over time</span>
                  </li>
                </ul>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <p className="text-xs font-medium text-slate-900 mb-1">Example</p>
                  <p className="text-xs text-slate-600">
                    Detecting potential break-ins or unsafe access patterns without constant human surveillance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section 
          ref={(el) => { sectionRefs.current[3] = el; }}
          className="relative py-32 bg-kmg-bg overflow-hidden"
        >
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
            <div className="absolute bottom-1/3 left-1/3 w-96 h-96 bg-kmg-accent/5 rounded-full blur-[150px]" />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto mb-16 text-center">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
                Why Cameras and Sensors Beat <span className="text-gradient">Manual Monitoring</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="p-8 bg-white rounded-xl shadow-card border border-slate-200">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Human Team with Checklist</h3>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-3">
                    <span className="text-slate-400 mt-1">•</span>
                    <span>At specific times</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-slate-400 mt-1">•</span>
                    <span>From limited angles</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-slate-400 mt-1">•</span>
                    <span>With subjective judgment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-slate-400 mt-1">•</span>
                    <span>Without historical context</span>
                  </li>
                </ul>
              </div>

              <div className="p-8 bg-white rounded-xl shadow-card border border-slate-200">
                <h3 className="text-xl font-bold text-slate-900 mb-6">The System</h3>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-3">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span>Continuously</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span>Objectively</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span>Across multiple signals at once</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-kmg-primary mt-1">•</span>
                    <span>With perfect memory</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-12 text-center max-w-3xl mx-auto">
              <p className="text-lg text-slate-600">
                This enables analytics that humans simply cannot gather reliably.
              </p>
            </div>
          </div>
        </section>

        {/* Analytics Section */}
        <section 
          ref={(el) => { sectionRefs.current[4] = el; }}
          className="relative py-32 bg-white overflow-hidden"
        >
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
            <div className="absolute top-1/2 right-0 w-96 h-96 bg-kmg-primary/5 rounded-full blur-[150px]" />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto mb-16">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
                Analytics and <span className="text-gradient">Preventative Maintenance</span>
              </h2>
              <p className="text-lg text-slate-600">
                Over time, the system builds a historical record of how environments behave when healthy.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-12">
              <div className="p-6 bg-white rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-2">Predictive Maintenance</h3>
                <p className="text-sm text-slate-600">Instead of reactive repair</p>
              </div>
              <div className="p-6 bg-white rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-2">Pattern Recognition</h3>
                <p className="text-sm text-slate-600">Across locations</p>
              </div>
              <div className="p-6 bg-white rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-2">Cost Forecasting</h3>
                <p className="text-sm text-slate-600">Plan ahead with data</p>
              </div>
              <div className="p-6 bg-white rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-2">Asset Benchmarking</h3>
                <p className="text-sm text-slate-600">Compare and optimize</p>
              </div>
            </div>

            <div className="max-w-4xl mx-auto bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-8 text-white shadow-card">
              <p className="text-lg font-medium mb-2">
                Instead of asking <span className="text-red-300">"What went wrong?"</span>
              </p>
              <p className="text-xl font-bold">
                Teams can ask <span className="text-green-300">"What is starting to change?"</span>
              </p>
            </div>
          </div>
        </section>

        {/* Cost Savings */}
        <section 
          ref={(el) => { sectionRefs.current[5] = el; }}
          className="relative py-32 bg-kmg-bg overflow-hidden"
        >
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
            <div className="absolute bottom-1/3 left-1/3 w-96 h-96 bg-kmg-accent/5 rounded-full blur-[150px]" />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto mb-16">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-kmg-primary/10 rounded-lg">
                  <DollarSign className="w-6 h-6 text-kmg-primary" />
                </div>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900">
                  Cost Savings and <span className="text-gradient">ROI</span>
                </h2>
              </div>
              <p className="text-lg text-slate-600">
                The largest costs in physical operations come from unplanned downtime, emergency repairs, asset replacement, and human labor for routine checks.
              </p>
            </div>

            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-card p-8 border border-slate-200 mb-8">
              <p className="text-slate-900 font-medium mb-4">By detecting issues earlier, organizations reduce:</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-slate-600">
                  <span className="text-kmg-primary">•</span>
                  <span>Repair severity</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <span className="text-kmg-primary">•</span>
                  <span>Production loss</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <span className="text-kmg-primary">•</span>
                  <span>Labor hours</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <span className="text-kmg-primary">•</span>
                  <span>Insurance claims</span>
                </div>
              </div>
            </div>

            <div className="max-w-4xl mx-auto text-center p-8 bg-gradient-to-r from-kmg-primary to-kmg-accent rounded-xl text-white shadow-card">
              <p className="text-2xl font-bold">Preventative action is always cheaper than recovery.</p>
            </div>
          </div>
        </section>

        {/* Industries */}
        <section 
          ref={(el) => { sectionRefs.current[6] = el; }}
          className="relative py-32 bg-white overflow-hidden"
        >
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
            <div className="absolute top-1/2 right-0 w-96 h-96 bg-kmg-primary/5 rounded-full blur-[150px]" />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto mb-16">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
                Industries This <span className="text-gradient">Applies To</span>
              </h2>
              <p className="text-lg text-slate-600">
                The applications are intentionally broad. Any environment with physical assets, people, or time-sensitive operations benefits from continuous sensing.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
              {industries.map((industry, index) => {
                const Icon = industry.icon;
                return (
                  <div
                    key={index}
                    className="flex items-center gap-4 p-5 bg-white rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 border border-slate-200"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-kmg-primary/10">
                      <Icon className="w-5 h-5 text-kmg-primary" />
                    </div>
                    <span className="font-medium text-slate-900 text-sm">{industry.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Second Image with Content */}
        <section className="relative py-16 bg-kmg-bg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  From Data to Action
                </h3>
                <p className="text-slate-600 mb-6">
                  The system doesn't just collect data—it transforms it into actionable insights. By understanding normal patterns and detecting deviations early, operations teams can prevent costly failures and optimize performance across their entire infrastructure.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-card border border-slate-200">
                    <p className="text-2xl font-bold text-kmg-primary mb-1">24/7</p>
                    <p className="text-xs text-slate-600">Continuous Monitoring</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-card border border-slate-200">
                    <p className="text-2xl font-bold text-kmg-primary mb-1">Real-time</p>
                    <p className="text-xs text-slate-600">Alert System</p>
                  </div>
                </div>
              </div>
              <div className="relative rounded-xl overflow-hidden shadow-card order-1 lg:order-2">
                <img
                  src="/caseStudy/caseStudy2.png"
                  alt="Sensor Intelligence System in Action"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why This Matters */}
        <section 
          ref={(el) => { sectionRefs.current[7] = el; }}
          className="relative py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden"
        >
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <div className="absolute top-0 right-0 w-96 h-96 bg-kmg-primary/20 rounded-full blur-[150px]" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-kmg-accent/20 rounded-full blur-[150px]" />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Why This <span className="text-gradient">Matters</span>
              </h2>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                Most companies already own the physical infrastructure. What they lack is awareness.
              </p>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 shadow-card">
                <p className="text-2xl font-bold mb-3">This system turns physical space into data.</p>
                <p className="text-lg text-slate-300">
                  Not for novelty. For reliability, efficiency, and foresight.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-32 bg-white overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
            <div className="absolute top-1/3 right-0 w-96 h-96 bg-kmg-primary/5 rounded-full blur-[150px]" />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              Ready to Transform Your Operations?
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              Learn how real-time vision and sensor intelligence can prevent issues before they become problems.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/#contact')}
                className="px-8 py-3 bg-kmg-primary text-white rounded-full font-semibold hover:bg-kmg-primary-dark transition-all duration-300 hover:scale-105 shadow-card"
              >
                Get in Touch
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-8 py-3 bg-slate-100 text-slate-900 rounded-full font-semibold hover:bg-slate-200 transition-all duration-300"
              >
                Back to Home
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CaseStudy;
